import { ReloadOutlined } from '@ant-design/icons';
import type { SettleItemRow, SettleRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { SETTLE_STATUS_META, formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Drawer, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

type StatusFilter = 'ALL' | 'PENDING' | 'CONFIRMED' | 'PAID';

// 状态表统一在 `@hq/utils`（三端曾各存一份，配色还分叉）
const STATUS_META = SETTLE_STATUS_META;

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '待确认', value: 'PENDING' },
  { label: '已确认', value: 'CONFIRMED' },
  { label: '已付清', value: 'PAID' }
];

/** 对账结算：列表 + 明细（只读，付款与开票在后续批次接入）。 */
export default function SettleList() {
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['settle', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.billing.settle.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        settleStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['settle', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.billing.settle.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<SettleRow> = [
    {
      title: '对账单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '结算期间', dataIndex: 'settlePeriod', width: 130, render: (value: string | null) => value || '-' },
    { title: '结算对象', dataIndex: 'payeeName', width: 180, render: (value: string | null) => value || '-' },
    { title: '结算依据', dataIndex: 'settleBasis', width: 130, render: (value: string | null) => value || '-' },
    { title: '应结金额', dataIndex: 'settleAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '应付金额', dataIndex: 'payableAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    { title: '已付金额', dataIndex: 'paidAmount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '状态',
      dataIndex: 'settleStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '确认时间', dataIndex: 'confirmAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<SettleItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 60 },
    { title: '业务类型', dataIndex: 'bizType', width: 120, render: (value: string | null) => value || '-' },
    { title: '业务单号', dataIndex: 'bizNo', width: 190, render: (value: string | null) => value || '-' },
    { title: '费用类型', dataIndex: 'feeType', width: 110, render: (value: string | null) => value || '-' },
    { title: '科室', dataIndex: 'deptName', width: 130, render: (value: string | null) => value || '-' },
    { title: '物资', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '数量', dataIndex: 'qty', width: 90, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '方向',
      dataIndex: 'direction',
      width: 90,
      render: (value: number) => (value < 0 ? <Tag color="red">扣减</Tag> : <Tag color="green">计费</Tag>)
    },
    { title: '发生时间', dataIndex: 'occurAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  return (
    <Card
      className="page-card"
      title="对账结算"
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
          刷新
        </Button>
      }
    >
      <Segmented<StatusFilter> value={status} options={FILTERS} onChange={(value) => setStatus(value)} style={{ marginBottom: 12 }} />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="对账单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<SettleRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无对账单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.account.bizNo ?? '对账单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={900}
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="期间">{detail.data.account.settlePeriod || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.account.settleStatus]?.text ?? detail.data.account.settleStatus}
              </Descriptions.Item>
              <Descriptions.Item label="结算对象">{detail.data.account.payeeName || '-'}</Descriptions.Item>
              <Descriptions.Item label="结算依据">{detail.data.account.settleBasis || '-'}</Descriptions.Item>
              <Descriptions.Item label="应结">{formatAmount(detail.data.account.settleAmount)}</Descriptions.Item>
              <Descriptions.Item label="折扣">{formatAmount(detail.data.account.discountAmount)}</Descriptions.Item>
              <Descriptions.Item label="退货扣减">{formatAmount(detail.data.account.returnAmount)}</Descriptions.Item>
              <Descriptions.Item label="税额">{formatAmount(detail.data.account.taxAmount)}</Descriptions.Item>
              <Descriptions.Item label="应付">{formatAmount(detail.data.account.payableAmount)}</Descriptions.Item>
              <Descriptions.Item label="已付">{formatAmount(detail.data.account.paidAmount)}</Descriptions.Item>
            </Descriptions>
            <Table<SettleItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
